(function() {
  'use strict';

  // Debug flag - set to false for production
  const DEBUG = false;
  const debug = (...args) => DEBUG && console.log(...args);
  const debugWarn = (...args) => DEBUG && console.warn(...args);

  debug('🎴 Holographic Cards JS - Loading...');

  // Utility functions
  const clamp = (num, min = 0, max = 100) => Math.min(Math.max(num, min), max);
  const round = (num, places = 2) => Math.round(num * Math.pow(10, places)) / Math.pow(10, places);
  const adjust = (value, fromMin, fromMax, toMin, toMax) => {
    return ((value - fromMin) / (fromMax - fromMin)) * (toMax - toMin) + toMin;
  };

  // Spring physics animation helper
  class Spring {
    constructor(initialValue, config = {}) {
      this.value = typeof initialValue === 'object' ? {...initialValue} : initialValue;
      this.target = typeof initialValue === 'object' ? {...initialValue} : initialValue;
      this.velocity = typeof initialValue === 'object'
        ? Object.keys(initialValue).reduce((acc, key) => ({...acc, [key]: 0}), {})
        : 0;
      this.stiffness = config.stiffness || 0.066;
      this.damping = config.damping || 0.25;
    }

    set(newTarget, options = {}) {
      this.target = typeof newTarget === 'object' ? {...newTarget} : newTarget;
      if (options.hard) {
        this.value = typeof newTarget === 'object' ? {...newTarget} : newTarget;
        this.velocity = typeof newTarget === 'object'
          ? Object.keys(newTarget).reduce((acc, key) => ({...acc, [key]: 0}), {})
          : 0;
      }
    }

    update() {
      if (typeof this.value === 'object') {
        Object.keys(this.value).forEach(key => {
          const force = (this.target[key] - this.value[key]) * this.stiffness;
          this.velocity[key] += force;
          this.velocity[key] *= (1 - this.damping);
          this.value[key] += this.velocity[key];
        });
      } else {
        const force = (this.target - this.value) * this.stiffness;
        this.velocity += force;
        this.velocity *= (1 - this.damping);
        this.value += this.velocity;
      }
      return this.value;
    }
  }

  // Holographic Card class
  class HolographicCard {
    constructor(element) {
      this.card = element;
      this.rotator = element.querySelector('.card__rotator');
      this.translater = element.querySelector('.card__translater');
      this.shine = element.querySelector('.card__shine');
      this.glare = element.querySelector('.card__glare');

      // Debug info
      this.debugName = element.dataset.debugName || 'Unknown';

      if (!this.rotator) {
        console.error(`❌ No .card__rotator found for ${this.debugName}`);
        return;
      }

      // State
      this.active = false;
      this.interacting = false;
      this.flipped = false;
      this.isVisible = true;
      this.isPaused = false;

      // Cache for DOM queries (Optimization #5)
      this.cachedRect = null;
      this.rectCacheValid = false;

      // Last CSS values for debouncing (Optimization #3)
      this.lastCSS = {
        pointerX: 50,
        pointerY: 50,
        rotateX: 0,
        rotateY: 0,
        backgroundX: 50,
        backgroundY: 50,
        scale: 1,
        translateX: 0,
        translateY: 0
      };

      // Spring animations
      this.springRotate = new Spring(
        { x: 0, y: 0 },
        { stiffness: 0.066, damping: 0.25 }
      );

      this.springGlare = new Spring(
        { x: 50, y: 50, o: 0 },
        { stiffness: 0.066, damping: 0.25 }
      );

      this.springBackground = new Spring(
        { x: 50, y: 50 },
        { stiffness: 0.066, damping: 0.25 }
      );

      this.springRotateDelta = new Spring(
        { x: 0, y: 0 },
        { stiffness: 0.033, damping: 0.45 }
      );

      this.springTranslate = new Spring(
        { x: 0, y: 0 },
        { stiffness: 0.033, damping: 0.45 }
      );

      this.springScale = new Spring(
        1,
        { stiffness: 0.033, damping: 0.45 }
      );

      this.animationFrame = null;

      this.init();
    }

    init() {
      // Mouse events
      this.rotator.addEventListener('pointermove', this.interact.bind(this));
      this.rotator.addEventListener('mouseout', this.interactEnd.bind(this));

      // Touch events
      this.rotator.addEventListener('touchmove', this.interact.bind(this), { passive: false });
      this.rotator.addEventListener('touchend', this.interactEnd.bind(this));

      // Click/tap to activate
      this.rotator.addEventListener('click', this.activate.bind(this));

      // Invalidate rect cache on resize — handled by global listener in initCards()
      this.getCachedRect();

      // Mark as initialized to prevent flash
      requestAnimationFrame(() => {
        this.card.classList.add('initialized');
      });

      // Start animation loop
      this.animate();
    }

    // Cache getBoundingClientRect for performance (Optimization #5)
    getCachedRect() {
      if (!this.rectCacheValid || !this.cachedRect) {
        this.cachedRect = this.rotator.getBoundingClientRect();
        this.rectCacheValid = true;
      }
      return this.cachedRect;
    }

    interact(e) {
      // Prevent default touch behavior
      if (e.type === 'touchmove') {
        e.preventDefault();
        e.clientX = e.touches[0].clientX;
        e.clientY = e.touches[0].clientY;
      }

      // Mark as interacting
      if (!this.interacting) {
        this.interacting = true;
        this.card.classList.add('interacting');

        // Restart animation if it was stopped (Optimization #2 fix)
        if (!this.animationFrame) {
          this.animate();
        }
      }

      // Use cached rect (Optimization #5)
      const rect = this.getCachedRect();
      const absolute = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };

      const percent = {
        x: clamp(round((100 / rect.width) * absolute.x)),
        y: clamp(round((100 / rect.height) * absolute.y)),
      };

      const center = {
        x: percent.x - 50,
        y: percent.y - 50,
      };

      // Update springs with new interaction data
      this.updateSprings(
        {
          x: adjust(percent.x, 0, 100, 37, 63),
          y: adjust(percent.y, 0, 100, 33, 67),
        },
        {
          x: round(-(center.x / 3.5)),
          y: round(center.y / 2),
        },
        {
          x: round(percent.x),
          y: round(percent.y),
          o: 1,
        }
      );
    }

    interactEnd() {
      setTimeout(() => {
        this.interacting = false;
        this.card.classList.remove('interacting');

        // Reset springs to neutral position with slower spring values
        this.springRotate.stiffness = 0.033;
        this.springRotate.damping = 0.15;
        this.springRotate.set({ x: 0, y: 0 });

        this.springGlare.stiffness = 0.033;
        this.springGlare.damping = 0.15;
        this.springGlare.set({ x: 50, y: 50, o: 0 });

        this.springBackground.stiffness = 0.033;
        this.springBackground.damping = 0.15;
        this.springBackground.set({ x: 50, y: 50 });
      }, 100);
    }

    activate(e) {
      e.preventDefault();
      e.stopPropagation();

      if (this.active) {
        // Card is already active, toggle flip
        this.toggleFlip();
      } else {
        // Deactivate other cards
        document.querySelectorAll('.card.active').forEach(card => {
          if (card !== this.card) {
            const cardInstance = card._cardInstance;
            if (cardInstance) cardInstance.deactivate();
          }
        });

        // Activate this card
        this.active = true;
        this.flipped = true;
        this.card.classList.add('active');
        this.card.setAttribute('role', 'dialog');
        this.card.setAttribute('aria-modal', 'true');
        this.card.setAttribute('aria-label', `${this.debugName} trading card details`);

        // Flip to show back (X-axis rotation for left-right flip)
        this.springRotateDelta.set({ x: 180, y: 0 });

        // Scale and center
        this.popover();

        // Trap focus
        this.trapFocus();

        // Restart animation if it was stopped (Optimization #2 fix)
        if (!this.animationFrame) {
          this.animate();
        }
      }
    }

    toggleFlip() {
      this.flipped = !this.flipped;
      // Use X-axis rotation for left-right flip
      const targetX = this.flipped ? 180 : 0;
      this.springRotateDelta.set({ x: targetX, y: 0 });

      // Update ARIA label
      const side = this.flipped ? 'back' : 'front';
      this.card.setAttribute('aria-label', `${this.debugName} trading card, showing ${side}`);
    }

    deactivate() {
      this.active = false;
      this.flipped = false;
      this.card.classList.remove('active');
      this.card.removeAttribute('role');
      this.card.removeAttribute('aria-modal');
      this.card.removeAttribute('aria-label');

      this.retreat();
      this.releaseFocus();
    }

    trapFocus() {
      // Store previously focused element
      this.previouslyFocused = document.activeElement;

      // Focus the card
      this.rotator.focus();

      // Add focus trap listener
      this.focusTrapHandler = (e) => {
        if (e.key === 'Tab') {
          e.preventDefault();
        }
      };

      document.addEventListener('keydown', this.focusTrapHandler);
    }

    releaseFocus() {
      // Remove focus trap
      if (this.focusTrapHandler) {
        document.removeEventListener('keydown', this.focusTrapHandler);
        this.focusTrapHandler = null;
      }

      // Return focus to previously focused element
      if (this.previouslyFocused) {
        this.previouslyFocused.focus();
        this.previouslyFocused = null;
      }
    }

    popover() {
      const rect = this.card.getBoundingClientRect();

      // Get scale values from CSS custom properties
      const section = this.card.closest('.holographic-cards-section');
      const restScale = parseFloat(getComputedStyle(section).getPropertyValue('--card-rest-scale') || '0.186');
      const expandedScale = parseFloat(getComputedStyle(section).getPropertyValue('--card-expanded-scale') || '0.5');

      // Calculate scale to fit viewport
      const scaleW = (window.innerWidth / rect.width) * 0.9;
      const scaleH = (window.innerHeight / rect.height) * 0.9;
      const scaleF = expandedScale / restScale; // Scale multiplier from rest to expanded

      // Center and scale the card
      this.setCenter();
      this.springRotateDelta.set({ x: 180, y: 0 });
      this.springScale.set(Math.min(scaleW, scaleH, scaleF));

      // Reset interaction springs
      this.interactEnd();
    }

    retreat() {
      // Return to grid position
      this.springScale.set(1);
      this.springTranslate.set({ x: 0, y: 0 });
      this.springRotateDelta.set({ x: 0, y: 0 });
      this.interactEnd();
    }

    setCenter() {
      const rect = this.card.getBoundingClientRect();
      const delta = {
        x: round(window.innerWidth / 2 - rect.x - rect.width / 2),
        y: round(window.innerHeight / 2 - rect.y - rect.height / 2),
      };
      this.springTranslate.set(delta);
    }

    updateSprings(background, rotate, glare) {
      // Set interaction spring values
      this.springBackground.stiffness = 0.066;
      this.springBackground.damping = 0.25;
      this.springRotate.stiffness = 0.066;
      this.springRotate.damping = 0.25;
      this.springGlare.stiffness = 0.066;
      this.springGlare.damping = 0.25;

      // Update targets
      this.springBackground.set(background);
      this.springRotate.set(rotate);
      this.springGlare.set(glare);
    }

    pause() {
      if (!this.isPaused) {
        this.isPaused = true;
        this.card.classList.add('paused');
      }
    }

    resume() {
      if (this.isPaused) {
        this.isPaused = false;
        this.card.classList.remove('paused');
        // Restart loop — it was truly stopped in animate() when paused
        if (!this.animationFrame) {
          this.animate();
        }
      }
    }

    animate() {
      // Skip animation if paused and not active
      if (this.isPaused && !this.active && !this.interacting) {
        this.animationFrame = null; // Truly stop — resume() will restart
        return;
      }

      // Update all springs
      const rotate = this.springRotate.update();
      const rotateDelta = this.springRotateDelta.update();
      const glare = this.springGlare.update();
      const background = this.springBackground.update();
      const translate = this.springTranslate.update();
      const scale = this.springScale.update();

      // Optimization #2: Check if springs have settled (stopped moving)
      // Only check when NOT active and NOT interacting
      if (!this.active && !this.interacting) {
        const springsAtRest =
          Math.abs(this.springRotate.velocity.x) < 0.01 &&
          Math.abs(this.springRotate.velocity.y) < 0.01 &&
          Math.abs(this.springGlare.velocity.x) < 0.01 &&
          Math.abs(this.springGlare.velocity.y) < 0.01 &&
          Math.abs(this.springGlare.velocity.o) < 0.01 &&
          Math.abs(this.springScale.velocity) < 0.001 &&
          Math.abs(this.springTranslate.velocity.x) < 0.1 &&
          Math.abs(this.springTranslate.velocity.y) < 0.1 &&
          Math.abs(this.springRotateDelta.velocity.x) < 0.01 &&
          Math.abs(this.springRotateDelta.velocity.y) < 0.01 &&
          Math.abs(this.springBackground.velocity.x) < 0.01 &&
          Math.abs(this.springBackground.velocity.y) < 0.01;

        if (springsAtRest) {
          // Springs have settled, pause until next interaction
          debug(`${this.debugName}: Springs settled, pausing animation`);
          return; // Don't schedule next frame
        }
      }

      // Calculate distance from center (for intensity effects)
      const pointerFromCenter = clamp(
        Math.sqrt(
          Math.pow(glare.y - 50, 2) + Math.pow(glare.x - 50, 2)
        ) / 50,
        0,
        1
      );

      // Optimization #3: Debounce CSS updates (threshold: 0.5 for %, 0.5deg for rotation, 0.001 for scale)
      const rotateX = rotate.x + rotateDelta.x;
      const rotateY = rotate.y + rotateDelta.y;

      // Update only changed values to reduce style recalculations
      if (Math.abs(glare.x - this.lastCSS.pointerX) > 0.5) {
        this.card.style.setProperty('--pointer-x', glare.x + '%');
        this.card.style.setProperty('--pointer-from-left', glare.x / 100);
        this.lastCSS.pointerX = glare.x;
      }

      if (Math.abs(glare.y - this.lastCSS.pointerY) > 0.5) {
        this.card.style.setProperty('--pointer-y', glare.y + '%');
        this.card.style.setProperty('--pointer-from-top', glare.y / 100);
        this.lastCSS.pointerY = glare.y;
      }

      this.card.style.setProperty('--pointer-from-center', pointerFromCenter);
      this.card.style.setProperty('--card-opacity', glare.o);

      if (Math.abs(rotateX - this.lastCSS.rotateX) > 0.5) {
        this.card.style.setProperty('--rotate-x', rotateX + 'deg');
        this.lastCSS.rotateX = rotateX;
      }

      if (Math.abs(rotateY - this.lastCSS.rotateY) > 0.5) {
        this.card.style.setProperty('--rotate-y', rotateY + 'deg');
        this.lastCSS.rotateY = rotateY;
      }

      if (Math.abs(background.x - this.lastCSS.backgroundX) > 0.5) {
        this.card.style.setProperty('--background-x', background.x + '%');
        this.lastCSS.backgroundX = background.x;
      }

      if (Math.abs(background.y - this.lastCSS.backgroundY) > 0.5) {
        this.card.style.setProperty('--background-y', background.y + '%');
        this.lastCSS.backgroundY = background.y;
      }

      if (Math.abs(scale - this.lastCSS.scale) > 0.001) {
        this.card.style.setProperty('--card-scale', scale);
        this.lastCSS.scale = scale;
      }

      if (Math.abs(translate.x - this.lastCSS.translateX) > 0.5) {
        this.card.style.setProperty('--translate-x', translate.x + 'px');
        this.lastCSS.translateX = translate.x;
      }

      if (Math.abs(translate.y - this.lastCSS.translateY) > 0.5) {
        this.card.style.setProperty('--translate-y', translate.y + 'px');
        this.lastCSS.translateY = translate.y;
      }

      // Continue animation loop
      this.animationFrame = requestAnimationFrame(this.animate.bind(this));
    }

    destroy() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }
      this.releaseFocus();
    }
  }

  // Intersection Observer for lazy loading and animation pausing
  let cardObserver = null;

  function createIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      debugWarn('⚠️ IntersectionObserver not supported, animations will run continuously');
      return null;
    }

    const options = {
      root: null,
      rootMargin: '50px',
      threshold: 0.01
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const card = entry.target;
        const instance = card._cardInstance;

        if (!instance) return;

        if (entry.isIntersecting) {
          instance.isVisible = true;
          instance.resume();
        } else {
          instance.isVisible = false;
          // Only pause if not active
          if (!instance.active) {
            instance.pause();
          }
        }
      });
    }, options);

    return observer;
  }

  // Initialize all cards on page
  function initCards() {
    debug('🎴 Initializing all holographic cards...');
    const cards = document.querySelectorAll('.holographic-card');

    if (cards.length === 0) {
      debugWarn('⚠️ No .holographic-card elements found on page');
      return;
    }

    debug(`📦 Found ${cards.length} cards to initialize`);

    // Create intersection observer for performance
    cardObserver = createIntersectionObserver();

    cards.forEach((card, index) => {
      // Destroy existing instance if present
      if (card._cardInstance) {
        card._cardInstance.destroy();
      }

      // Create new instance
      const instance = new HolographicCard(card);
      card._cardInstance = instance;

      // Observe card for visibility
      if (cardObserver) {
        cardObserver.observe(card);
      }
    });

    debug('✅ All cards initialized');
  }

  // Global event listeners

  // ESC key closes active card
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const activeCard = document.querySelector('.card.active');
      if (activeCard && activeCard._cardInstance) {
        activeCard._cardInstance.deactivate();
      }
    }
  });

  // Click outside card closes it
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.card')) {
      const activeCard = document.querySelector('.card.active');
      if (activeCard && activeCard._cardInstance) {
        activeCard._cardInstance.deactivate();
      }
    }
  });

  // Handle window resize — single listener covers all cards
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      // Invalidate all rect caches in one pass
      document.querySelectorAll('.holographic-card').forEach(card => {
        if (card._cardInstance) card._cardInstance.rectCacheValid = false;
      });
      // Re-center active card if any
      const activeCard = document.querySelector('.card.active');
      if (activeCard && activeCard._cardInstance) {
        activeCard._cardInstance.setCenter();
      }
    }, 150);
  });

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCards);
  } else {
    initCards();
  }

  // Shopify Theme Editor support
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      // Disconnect observer before reinitializing
      if (cardObserver) {
        cardObserver.disconnect();
      }
      initCards();
    });

    document.addEventListener('shopify:section:unload', () => {
      document.querySelectorAll('.holographic-card').forEach(card => {
        if (card._cardInstance) {
          card._cardInstance.destroy();
        }
      });
      if (cardObserver) {
        cardObserver.disconnect();
      }
    });
  }

  debug('✅ Holographic Cards JS - Loaded successfully');

})();

// ============================================================================
// DIMENSION FIX - Ensures shine and glare layers have proper dimensions
// Runs ONCE after page load, uses requestIdleCallback for performance
// ============================================================================

(function() {
  'use strict';

  // Use same debug flag as main module
  const DEBUG = false;
  const debug = (...args) => DEBUG && console.log(...args);
  const debugWarn = (...args) => DEBUG && console.warn(...args);

  debug('[DIMENSION FIX] Initializing...');

  // Process a single card's dimensions
  function processCard(card, index) {
    // Get ALL shine and glare elements (both on front and back)
    const shineElements = card.querySelectorAll('.card__shine');
    const glareElements = card.querySelectorAll('.card__glare');
    const front = card.querySelector('.card__front');

    if (shineElements.length === 0 || glareElements.length === 0 || !front) {
      debugWarn(`[DIMENSION FIX] Card ${index}: Missing elements`);
      return;
    }

    const frontRect = front.getBoundingClientRect();
    debug(`[DIMENSION FIX] Card ${index}:`, {
      width: frontRect.width,
      height: frontRect.height,
      shineCount: shineElements.length,
      glareCount: glareElements.length
    });

      // Apply fix to ALL shine and glare elements
      shineElements.forEach((shine) => {
        // Clear any old inline styles first
        shine.removeAttribute('style');

        // Set dimension properties with !important
        shine.style.setProperty('position', 'absolute', 'important');
        shine.style.setProperty('inset', '0', 'important');
        shine.style.setProperty('width', '100%', 'important');
        shine.style.setProperty('height', '100%', 'important');
        shine.style.setProperty('min-width', `${frontRect.width}px`, 'important');
        shine.style.setProperty('min-height', `${frontRect.height}px`, 'important');
        shine.style.setProperty('display', 'block', 'important');
        shine.style.setProperty('visibility', 'visible', 'important');

        // Set visual properties WITHOUT !important (allows CSS to work, animations to override)
        // Subtle rainbow gradient below image layer
        shine.style.zIndex = '1';
        shine.style.background = 'repeating-linear-gradient(-45deg, rgb(247, 23, 23) 0%, rgb(255, 200, 0) 10%, rgb(249, 255, 0) 20%, rgb(16, 255, 0) 30%, rgb(0, 255, 216) 40%, rgb(0, 129, 255) 50%, rgb(128, 0, 255) 60%, rgb(255, 70, 187) 70%, rgb(247, 23, 23) 80%)';
        shine.style.backgroundSize = '300% 300%';
        shine.style.backgroundPosition = 'var(--background-x, 50%) var(--background-y, 50%)';
        shine.style.opacity = '0.3';
        shine.style.mixBlendMode = 'color-dodge';
        shine.style.pointerEvents = 'none';
        shine.style.borderRadius = 'var(--card-radius)';
      });

      // Apply fix to ALL glare elements
      glareElements.forEach((glare) => {
        // Clear any old inline styles first
        glare.removeAttribute('style');

        // Glare dimensions
        glare.style.setProperty('position', 'absolute', 'important');
        glare.style.setProperty('inset', '0', 'important');
        glare.style.setProperty('width', '100%', 'important');
        glare.style.setProperty('height', '100%', 'important');
        glare.style.setProperty('min-width', `${frontRect.width}px`, 'important');
        glare.style.setProperty('min-height', `${frontRect.height}px`, 'important');
        glare.style.setProperty('display', 'block', 'important');
        glare.style.setProperty('visibility', 'visible', 'important');

        // Set visual properties WITHOUT !important (allows animations to work)
        // Subtle white spotlight on top of everything
        glare.style.zIndex = '4';
        glare.style.background = 'radial-gradient(circle at var(--pointer-x, 50%) var(--pointer-y, 50%), rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.2) 20%, rgba(255, 255, 255, 0.1) 40%, transparent 60%)';
        glare.style.opacity = '0.5';
        glare.style.mixBlendMode = 'overlay';
        glare.style.pointerEvents = 'none';
        glare.style.borderRadius = 'var(--card-radius)';
      });

    debug(`[DIMENSION FIX] Card ${index}: Fixed with subtle gradients on both sides`);
  }

  // Main function — single idle callback processes all cards at once
  function fixDimensions() {
    const cards = document.querySelectorAll('.card');

    const process = () => cards.forEach((card, index) => processCard(card, index));

    if ('requestIdleCallback' in window) {
      requestIdleCallback(process, { timeout: 2000 });
    } else {
      process();
    }
  }

  // Run once after page loads
  if (document.readyState === 'complete') {
    fixDimensions();
  } else {
    window.addEventListener('load', fixDimensions);
  }

  debug('[DIMENSION FIX] Initialized');
})();